import React, { ComponentType } from 'react';
import { useBreakpoints } from '@ux/responsive';
import Layer, { Layer as ImageLayer } from 'presentation/components/common/Layer';
import withLanguage from 'presentation/hocs/withLanguage';
import urlLanguageParse from 'presentation/utils/urlLanguageParse';
import findResponsiveItem from 'presentation/utils/findResponsiveItem';
import { Layout } from 'domain/constants/layout';
import { Colorway } from '@uds/theme-utils';
import { ResponsiveSet } from 'core/ribbons/primitives';

import Wash from './Wash';
import { classNames } from './Background.stylesheet';

export interface Background {
  fallback: {
    url: string;
  };
  mp4?: string;
  webm?: string;
  loop?: boolean;
}
export type BackgroundArray = ResponsiveSet<Background | ImageLayer>;

interface OuterProps {
  layout: Layout;
  background: BackgroundArray;
  duoLayout?: boolean;
  hasTiles?: boolean;
  colorway: Colorway;
  hideGradient?: boolean;
}
interface InnerProps extends OuterProps {
  currentLanguage: string;
}

const showFallbackImage = (video: HTMLVideoElement) => {
  video.autoplay = false;
  video.load();
};

export const PureBackground = ({
  layout,
  duoLayout,
  hasTiles,
  colorway,
  currentLanguage,
  background,
  hideGradient = false,
}: InnerProps) => {
  const activeBackground = findResponsiveItem(useBreakpoints(), background);
  const backgroundType = Array.isArray(activeBackground) ? 'image' : 'video';

  if (activeBackground != null) {
    return (
      <>
        {backgroundType === 'video' && (
          <div className={classNames.videoWrapper}>
            <video
              loop={activeBackground.loop ?? true}
              muted={true}
              autoPlay={true}
              playsInline={true}
              className={classNames.video}
              poster={urlLanguageParse(activeBackground.fallback.url, currentLanguage)}
              onEnded={(e) => showFallbackImage(e.currentTarget)}
            >
              <source src={activeBackground.mp4} type="video/mp4" />
              <source src={activeBackground.webm} type="video/webm" />
              <img
                loading="lazy"
                src={urlLanguageParse(activeBackground.fallback.url, currentLanguage)}
                alt="fallback"
                className={classNames.fallbackVideoImage}
              />
            </video>
          </div>
        )}
        {backgroundType === 'image' && (
          <>
            {activeBackground.map((layer: ImageLayer, i: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <Layer key={`background_layer_${i}`} layer={layer} />
            ))}
          </>
        )}
        {!hideGradient && <Wash colorway={colorway} layout={layout} duoLayout={duoLayout} hasTiles={hasTiles} />}
      </>
    );
  }

  return null;
};

PureBackground.displayName = 'Background';

export default withLanguage(PureBackground) as ComponentType<OuterProps>;
